import { apiClient } from '../../api/client';
import type { components } from '../../api/generated/schema';
import type { NewMessage } from '../../domain/conversations';
import { mapped } from '../errors';
import { toPersonRef } from '../mapping';
import type { ConversationsRepository } from './ConversationsRepository';
import {
  toConversation,
  toConversationSummary,
  toMessage,
  toMessagePhoto,
  toMessagePhotoUpload,
  toNewMessage,
} from './mapping';

type S = components['schemas'];

const enc = encodeURIComponent;
const path = (id: string) => `/api/v1/conversations/${enc(id)}/`;

/** The photo's size, so its message reserves room before it loads; none when it can't be read. */
async function imageSize(file: File): Promise<{ width?: number; height?: number }> {
  try {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
  } catch {
    return {};
  }
}

/** Every call goes to the contract; the stubbed ones answer `notYetAvailable` from the server. */
export const httpConversationsRepository: ConversationsRepository = {
  messagingEligibility: (personId: string) =>
    mapped(async () => {
      const body = await apiClient.get<S['MessagingEligibility']>(
        `/api/v1/people/${enc(personId)}/messaging/`,
      );
      return { canMessage: body.can_message };
    }),
  list: () =>
    mapped(async () =>
      (await apiClient.get<S['ConversationSummary'][]>('/api/v1/conversations/')).map(
        toConversationSummary,
      ),
    ),
  recipients: () =>
    mapped(async () =>
      (await apiClient.get<S['PersonRef'][]>('/api/v1/conversations/recipients/')).map(toPersonRef),
    ),
  get: (id: string) =>
    mapped(async () => toConversation(await apiClient.get<S['ConversationDetail']>(path(id)))),
  openDirect: (personId: string, listingId?: string | null) =>
    mapped(async () =>
      toConversation(
        await apiClient.send<S['ConversationDetail']>('POST', '/api/v1/conversations/direct/', {
          person_id: personId,
          listing_id: listingId ?? null,
        }),
      ),
    ),
  createGroup: (personIds: string[], name?: string | null, listingId?: string | null) =>
    mapped(async () =>
      toConversation(
        await apiClient.send<S['ConversationDetail']>('POST', '/api/v1/conversations/groups/', {
          person_ids: personIds,
          name: name ?? null,
          listing_id: listingId ?? null,
        }),
      ),
    ),
  rename: (id: string, name: string) =>
    mapped(async () =>
      toConversation(await apiClient.send<S['ConversationDetail']>('PATCH', path(id), { name })),
    ),
  send: (id: string, message: NewMessage) =>
    mapped(async () =>
      toMessage(
        await apiClient.send<S['Message']>('POST', `${path(id)}messages/`, toNewMessage(message)),
      ),
    ),
  takeBack: (messageId: string) =>
    mapped(async () =>
      toMessage(
        await apiClient.send<S['Message']>('POST', `/api/v1/messages/${enc(messageId)}/take-back/`),
      ),
    ),
  markRead: (id: string) =>
    mapped(async () => {
      await apiClient.send('POST', `${path(id)}read/`);
    }),
  addParticipant: (id: string, personId: string) =>
    mapped(async () =>
      toConversation(
        await apiClient.send<S['ConversationDetail']>('POST', `${path(id)}participants/`, {
          person_id: personId,
        }),
      ),
    ),
  removeParticipant: (id: string, personId: string) =>
    mapped(async () => {
      await apiClient.send('DELETE', `${path(id)}participants/${enc(personId)}/`);
    }),
  leave: (id: string) =>
    mapped(async () => {
      await apiClient.send('POST', `${path(id)}leave/`);
    }),
  requestMessagePhotoUpload: (filename: string) =>
    mapped(async () =>
      toMessagePhotoUpload(
        await apiClient.send<Record<string, unknown>>(
          'POST',
          '/api/v1/message-images/request-upload/',
          { filename },
        ),
      ),
    ),
  uploadMessagePhoto: (file: File) =>
    mapped(async () => {
      const upload = toMessagePhotoUpload(
        await apiClient.send<Record<string, unknown>>(
          'POST',
          '/api/v1/message-images/request-upload/',
          { filename: file.name },
        ),
      );
      await apiClient.putBlob(upload.uploadUrl, file);
      const size = await imageSize(file);
      return toMessagePhoto(
        await apiClient.send<S['MessagePhoto']>('POST', '/api/v1/message-images/confirm-upload/', {
          image_id: upload.imageId,
          blob_name: upload.blobName,
          ...size,
        }),
      );
    }),
  confirmMessagePhoto: (imageId: string, blobName: string, width?: number, height?: number) =>
    mapped(async () =>
      toMessagePhoto(
        await apiClient.send<S['MessagePhoto']>('POST', '/api/v1/message-images/confirm-upload/', {
          image_id: imageId,
          blob_name: blobName,
          ...(width !== undefined && { width }),
          ...(height !== undefined && { height }),
        }),
      ),
    ),
};
